import { addDays, addMonths, DateOnly } from '@if/core/date'
import { EProductsInsurance } from '@if/core/types'
import { ControlledNumberField, ControlledDateOnlyPicker, ControlledTextField } from '@if/mui'
import { Stack } from '@mui/material'
import { ProductsWorksheet } from '@src/types/WorksheetSchema'
import { useEffect, useMemo } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  control: Control<ProductsWorksheet>
  insurance: EProductsInsurance
  today: Date
  setValue: UseFormSetValue<ProductsWorksheet>
  hasContractEndDate: boolean
}

const ProductsInsuranceDetails = ({ insurance: name, control, today, setValue, hasContractEndDate }: Props) => {
  const { t } = useTranslation()

  const showDatePicker = useMemo(() => {
    const result = name !== EProductsInsurance.ExtendedWarranty
    return result
  }, [name])

  const term = useWatch({
    control,
    name: `${name}.term`,
  })

  const minDate = useMemo(() => {
    return isNaN(Number(term)) ? today : addMonths(today, Number(term))
  }, [today, term])

  const maxDate = useMemo(() => {
    return addDays(addMonths(today, isNaN(Number(term)) ? 12 : Number(term)), 21)
  }, [today, term])

  useEffect(() => {
    if (showDatePicker && term && !hasContractEndDate) {
      const termNumber = Number(term)
      if (!isNaN(termNumber)) {
        const calculatedEndDate = addMonths(today, termNumber)
        setValue(`${name}.endDate`, new DateOnly(calculatedEndDate), { shouldValidate: true })
      }
    }
  }, [term, showDatePicker, today, name, setValue, hasContractEndDate])
  return (
    <Stack spacing={2}>
      <ControlledTextField control={control} name={`${name}.police`} label={t('productsWorksheet.policy')} fullWidth />
      <ControlledTextField
        control={control}
        name={`${name}.term`}
        label={`${t('productsWorksheet.term')} (${t('common.months')})`}
      />
      {showDatePicker && (
        <ControlledDateOnlyPicker
          name={`${name}.endDate`}
          control={control}
          label={t('productsWorksheet.productInsuranceEndDate')}
          minDate={minDate}
          maxDate={maxDate}
          disabled={isNaN(Number(term)) || !term}
        />
      )}
      <ControlledNumberField isMoney control={control} name={`${name}.amount`} label={t('productsWorksheet.amount')} />
    </Stack>
  )
}

export default ProductsInsuranceDetails
